import * as documentsAPI from "app/api/documentsApi";
import { VerificationDocumentsHelper } from "app/helpers/verificationDocumentsHelper";
import {
  DocumentCategory,
  DocumentsForUpload,
  verificationDocumentMaxFileSizeMb,
} from "app/utils/const";
import React, { useRef, useState } from "react";
import { FormattedMessage, injectIntl, IntlShape, useIntl } from "react-intl";
import { connect } from "react-redux";
import DocumentGeneralVerificationGuidelines from "../DocumentGeneralVerificationGuidelines/DocumentGeneralVerificationGuidelines";
import FileSelected from "../FileSelected/FileSelected";
import IdCardGuidelines from "../IdCardGuidelines/IdCardGuidelines";
import PassportVerificationGuidelines from "../PassportGuidelines/PassportGuidelines";
import PoaGuidelines from "../PoaGuidelines/PoaGuidelines";
import SubmitForm from "../SubmitForm/SubmitForm";
import "./DocumentUploadForm.scss";

type Props = {
  setDocumentList: () => void;
  intl: IntlShape;
  title: string;
  content: any;
  selectedDocumentCategory: string;
  selectedIdentityDocumentCategory: string;
  onBackClick: () => void;
  onDocumentUpload: () => void;
};

const DocumentUploadForm: React.FC<Props> = ({
  setDocumentList,
  selectedDocumentCategory,
  selectedIdentityDocumentCategory,
  onBackClick,
  onDocumentUpload,
}) => {
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const intl = useIntl();
  const onAddFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      if (
        event.target.files[0].size >
        VerificationDocumentsHelper.convertMegabytesToBytes(
          verificationDocumentMaxFileSizeMb
        )
      ) {
        event.target.value = "";
        const errorMessage = intl.formatMessage(
          {
            id: "account-verification-max-file-size",
          },
          {
            verificationDocumentMaxFileSizeMb:
              verificationDocumentMaxFileSizeMb,
          }
        );
        setUploadError(errorMessage);
        onRemoveFile();
        return;
      } else {
        setUploadError("");
        setSelectedFileName(event.target.files[0].name);
        setSelectedFile(event.target.files[0]);
      }
    }
  };

  const onRemoveFile = () => {
    setSelectedFileName("");
    setSelectedFile(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("documentName", selectedFileName);
    formData.append("documentType", selectedDocumentCategory);
    // Only documents of type IDENTITY have a subtype
    formData.append(
      "documentSubType",
      selectedDocumentCategory === DocumentCategory.IDENTITY
        ? selectedIdentityDocumentCategory
        : "None"
    );

    setIsSubmitting(true);

    try {
      await documentsAPI.uploadDocument(formData);
      setDocumentList();
      onDocumentUpload();
      onRemoveFile(); // Clear after successful upload
    } catch (error: any) {
      console.log("error: ", error);
      if (
        error.response.data &&
        error.response.data.errors &&
        error.response.data.errors.length > 0
      ) {
        setUploadError(error.response.data.errors[0].messages[0]);
      } else {
        setUploadError("Error uploading document");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileSelected = selectedFileName.length > 0;

  const getheading = () => {
    switch (selectedIdentityDocumentCategory) {
      case DocumentsForUpload.PASSPORT:
        return (
          <FormattedMessage
            id="account-verification-passport-title"
            defaultMessage="Please make sure your passport meets the following criteria to complete verification"
          />
        );
      case DocumentsForUpload.DRIVING_LICENCE:
        return (
          <FormattedMessage
            id="account-verification-driving-licence-title"
            defaultMessage={
              "Please make sure your driving licence meets the following criteria to complete verification."
            }
          />
        );
      case DocumentsForUpload.ID_CARD_FRONT:
        return (
          <div className="id-card__heading">
            <span>
              <FormattedMessage
                id="account-verification-id-front-title"
                defaultMessage={
                  "Please make sure your ID card meets the following criteria to complete verification."
                }
              />
            </span>

            <span>
              <FormattedMessage
                id="account-verification-id-front-note"
                defaultMessage="We'll need to see the front first."
              />
            </span>
          </div>
        );
      case DocumentsForUpload.ID_CARD_BACK:
        return (
          <FormattedMessage
            id="account-verification-id-back-title"
            defaultMessage={"Please add the back of your identity card"}
          />
        );
      case DocumentsForUpload.POA:
        return (
          <FormattedMessage
            id="account-verification-proof-of-address-title"
            defaultMessage="Choose Proof Of Address Document"
          />
        );
      default:
        return <span>Not found</span>;
    }
  };
  const getContent = () => {
    switch (selectedIdentityDocumentCategory) {
      case DocumentsForUpload.PASSPORT:
        return <PassportVerificationGuidelines />;
      case DocumentsForUpload.DRIVING_LICENCE:
        return <PassportVerificationGuidelines />;
      case DocumentsForUpload.ID_CARD_FRONT:
        return <IdCardGuidelines side={"front"} />;
      case DocumentsForUpload.ID_CARD_BACK:
        return <IdCardGuidelines side={"back"} />;
      case DocumentsForUpload.POA:
        return <PoaGuidelines />;
      default:
        return (
          <FormattedMessage
            id="unknown-document-type"
            defaultMessage="Unknown Document: This document type is not recognized."
          />
        );
    }
  };
  const dropAreaRef = useRef<HTMLDivElement>(null);

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    // add styling to show the drop area
    dropAreaRef.current?.classList.add("drag-over");
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dropAreaRef.current?.classList.remove("drag-over");
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dropAreaRef.current?.classList.remove("drag-over");

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFileName(files[0].name);
      setSelectedFile(files[0]);
    }
  };
  return (
    <div className="document-upload-container">
      <div className="document-upload-container__heading">
        <h3>{getheading()}</h3>
      </div>
      <div className="document-upload-container__inner">
        <div>{getContent()}</div>
        <DocumentGeneralVerificationGuidelines />
        <form className="documents__form" onSubmit={onSubmit}>
          {!fileSelected && (
            <div
              ref={dropAreaRef}
              className="documents__form__file-upload__drag"
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => {
                const fileInput = document.querySelector(
                  ".documents__form__file-upload input[type='file']"
                ) as HTMLInputElement | null;
                if (fileInput) {
                  fileInput.click();
                }
              }}
            >
              {fileSelected && (
                <FileSelected
                  selectedFileName={selectedFileName}
                  onRemoveFile={onRemoveFile}
                />
              )}
              <FormattedMessage
                id="drag-files-text"
                defaultMessage={
                  "Drag & drop files here, or click to select files"
                }
              />
            </div>
          )}

          {fileSelected && (
            <FileSelected
              selectedFileName={selectedFileName}
              onRemoveFile={onRemoveFile}
            />
          )}
          <label
            className="documents__form__file-upload"
            style={{ display: "none" }}
          >
            <input onChange={onAddFile} type="file" />
          </label>

          <SubmitForm isSubmitting={isSubmitting} fileSelected={fileSelected} />
          {uploadError && (
            <div className="documents__error-msg">{uploadError}</div>
          )}
        </form>
      </div>
      <button
        className="identity-document-choice__button identity-document-choice__button__back"
        onClick={onBackClick}
      >
        <FormattedMessage
          id="account-verification-select-document-back-button"
          defaultMessage="Back"
        />
      </button>
    </div>
  );
};

const mapStateToProps = state => ({});

const mapDispatchToProps = (dispatch: any) => ({
  setDocumentList: () => {
    dispatch.documentListModel.setDocumentList();
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(DocumentUploadForm)) as any;
